export const OFFICIAL_NEUROGRES_GAMES = [
  "Move-It",
  "PingWins",
  "Two-Flags",
  "MaxIt",
  "ClimbUp",
  "Blocker",
] as const;

export type NeurogresGame = typeof OFFICIAL_NEUROGRES_GAMES[number];

export const OFFICIAL_NEUROGRES_SKILLS = [
  "Planning",
  "Efficiency",
  "SystematicThinking",
  "Accuracy",
  "TimeManagement",
  "QuickThinking",
  "Persistence",
  "TaskManagement",
  "Resourcefulness",
  "FlexibleThinking",
  "CopingWithChanges",
  "TakingInitiative",
  "LearningAptitude",
  "DevelopingAPlan",
  "IdentifyingOpportunities",
  "SystemicVision",
  "ResourceManagement",
  "Calculation",
  "PatternRecognition",
  "AnalysisAndDeduction",
  "SolvingComplexProblems",
  "LogicalThinking",
  "DealingWithTimePressure",
] as const;

export type NeurogresSkill = typeof OFFICIAL_NEUROGRES_SKILLS[number];

export const NEUROGRES_SKILL_ALIASES: Readonly<Record<string, NeurogresSkill>> = {
  planning: "Planning",
  efficiency: "Efficiency",
  systematic_thinking: "SystematicThinking",
  systematicthinking: "SystematicThinking",
  accuracy: "Accuracy",
  time_management: "TimeManagement",
  timemanagement: "TimeManagement",
  quick_thinking: "QuickThinking",
  quickthinking: "QuickThinking",
  persistence: "Persistence",
  task_management: "TaskManagement",
  taskmanagement: "TaskManagement",
  resourcefulness: "Resourcefulness",
  flexible_thinking: "FlexibleThinking",
  flexiblethinking: "FlexibleThinking",
  coping_with_changes: "CopingWithChanges",
  copingwithchanges: "CopingWithChanges",
  taking_initiative: "TakingInitiative",
  takinginitiative: "TakingInitiative",
  learning_aptitude: "LearningAptitude",
  learningaptitude: "LearningAptitude",
  developing_a_plan: "DevelopingAPlan",
  developingaplan: "DevelopingAPlan",
  identifying_opportunities: "IdentifyingOpportunities",
  identifyingopportunities: "IdentifyingOpportunities",
  systemic_vision: "SystemicVision",
  systemicvision: "SystemicVision",
  resource_management: "ResourceManagement",
  resourcemanagement: "ResourceManagement",
  calculation: "Calculation",
  pattern_recognition: "PatternRecognition",
  patternrecognition: "PatternRecognition",
  analysis_and_deduction: "AnalysisAndDeduction",
  analysisanddeduction: "AnalysisAndDeduction",
  solving_complex_problems: "SolvingComplexProblems",
  solvingcomplexproblems: "SolvingComplexProblems",
  logical_thinking: "LogicalThinking",
  logicalthinking: "LogicalThinking",
  dealing_with_time_pressure: "DealingWithTimePressure",
  dealingwithtimepressure: "DealingWithTimePressure",
};

export const SKILL_GAME_MAP: Readonly<Record<NeurogresSkill, readonly NeurogresGame[]>> = {
  Planning: ["ClimbUp"],
  Efficiency: ["Move-It"],
  SystematicThinking: ["Two-Flags"],
  Accuracy: ["Move-It"],
  TimeManagement: ["PingWins"],
  QuickThinking: ["PingWins"],
  Persistence: ["ClimbUp"],
  TaskManagement: ["ClimbUp"],
  Resourcefulness: ["Two-Flags"],
  FlexibleThinking: ["Two-Flags"],
  CopingWithChanges: ["MaxIt"],
  TakingInitiative: ["Move-It"],
  LearningAptitude: ["Two-Flags"],
  DevelopingAPlan: ["ClimbUp"],
  IdentifyingOpportunities: ["Two-Flags"],
  SystemicVision: ["MaxIt"],
  ResourceManagement: ["Blocker"],
  Calculation: ["MaxIt"],
  PatternRecognition: ["PingWins"],
  AnalysisAndDeduction: ["PingWins"],
  SolvingComplexProblems: ["MaxIt"],
  LogicalThinking: ["Two-Flags"],
  DealingWithTimePressure: ["Blocker"],
};

export const BLOCK_TO_SKILL_MAP: Readonly<Record<string, NeurogresSkill>> = {
  overthinking: "SolvingComplexProblems",
  procrastination: "Planning",
  anxiety: "CopingWithChanges",
  perfectionism: "FlexibleThinking",
  burnout: "TimeManagement",
  indecision: "DevelopingAPlan",
  fear: "TakingInitiative",
  confidence: "TakingInitiative",
  paralysis: "Planning",
  distraction: "ResourceManagement",
  pressure: "DealingWithTimePressure",
};

export function getGamesForSkill(skill: NeurogresSkill | string): readonly NeurogresGame[] {
  const normalized = normalizeSkillKey(skill);
  return normalized ? SKILL_GAME_MAP[normalized] || [] : [];
}

export function getSkillForBlock(block: string): NeurogresSkill | null {
  return BLOCK_TO_SKILL_MAP[String(block || "").trim().toLowerCase()] || null;
}

export function normalizeSkillKey(skill: NeurogresSkill | string | null | undefined): NeurogresSkill | null {
  const raw = String(skill || "").trim();
  if (!raw) return null;
  if (OFFICIAL_NEUROGRES_SKILLS.includes(raw as NeurogresSkill)) {
    return raw as NeurogresSkill;
  }
  return NEUROGRES_SKILL_ALIASES[raw.replace(/[\s-]+/g, "_").toLowerCase()] || null;
}
